import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { MODULES } from "@/data/constants";
import { CreditCard, Receipt, ExternalLink, ArrowRight, Loader2, FileText, Plus } from "lucide-react";

const NAME = Object.fromEntries(MODULES.map((m) => [m.key, m.name]));

export default function BillingPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const L = (fr, en) => (lang === "en" ? en : fr);

  useEffect(() => {
    api.get("/payments/subscription").then(({ data }) => setData(data)).catch(() => setData({ subscription: null, invoices: [] })).finally(() => setLoading(false));
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || L("Portail indisponible.", "Portal unavailable."));
      setPortalLoading(false);
    }
  };

  const sub = data?.subscription;
  const invoices = data?.invoices || [];
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", { year: "numeric", month: "short", day: "numeric" }); } catch { return d; } };

  return (
    <motion.div className="space-y-8" data-testid="billing-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-3xl font-extrabold text-white flex items-center gap-2.5"><Receipt className="w-6 h-6 text-[#EAB308]" /> {L("Facturation", "Billing")}</h1>
        <p className="mt-1 text-white/55">{L("Gérez votre abonnement, vos factures et votre moyen de paiement.", "Manage your subscription, invoices and payment method.")}</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-5 h-5 animate-spin" /> {L("Chargement…", "Loading…")}</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Résumé abonnement */}
          <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6" data-testid="billing-summary">
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#EAB308]" /> {L("Votre abonnement", "Your subscription")}</h2>
            <div className="mt-4 space-y-2.5 text-sm">
              <RowB k={L("Forfait", "Plan")} v={t(`dash.plan.${user.plan}`)} />
              <RowB k={L("Statut", "Status")} v={<span className="text-green-400 capitalize">{sub?.status || (L("démo", "demo"))}</span>} />
              <RowB k={L("Montant mensuel", "Monthly amount")} v={sub?.final_monthly_total != null ? `${sub.final_monthly_total} $` : "—"} />
              <RowB k={L("Modules", "Modules")} v={sub?.selected_modules?.length || 0} />
            </div>
            {sub?.selected_modules?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {sub.selected_modules.map((m) => <span key={m} className="rounded-full border border-white/12 bg-white/5 px-2 py-0.5 text-[11px] text-white/70">{NAME[m] || m}</span>)}
              </div>
            )}
            <div className="mt-5 space-y-2">
              <button onClick={openPortal} disabled={portalLoading} data-testid="open-stripe-portal" className="flex w-full items-center justify-center gap-2 rounded-md bg-[#EAB308] text-black py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} {L("Portail client Stripe", "Stripe customer portal")}
              </button>
              <button onClick={() => navigate("/tarification")} data-testid="change-plan" className="flex w-full items-center justify-center gap-2 rounded-md border border-white/12 bg-white/5 py-2.5 text-sm font-semibold text-white/80 hover:text-white transition-colors">
                <Plus className="w-4 h-4" /> {L("Changer / ajouter un module", "Change / add a module")}
              </button>
            </div>
            {!sub?.has_stripe && (
              <p className="mt-3 text-xs text-white/40">{L("Compte de démonstration — le portail Stripe est disponible après un premier paiement.", "Demo account — the Stripe portal is available after a first payment.")}</p>
            )}
          </div>

          {/* Historique des factures */}
          <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6 lg:col-span-2" data-testid="billing-invoices">
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-[#EAB308]" /> {L("Historique des factures", "Invoice history")}</h2>
            {invoices.length === 0 ? (
              <div className="mt-6 rounded-lg border border-dashed border-white/12 py-12 text-center" data-testid="invoices-empty">
                <p className="text-white/50">{L("Aucune facture pour le moment.", "No invoices yet.")}</p>
                <button onClick={() => navigate("/tarification")} className="mt-3 inline-flex items-center gap-1 text-sm text-[#EAB308] hover:underline">
                  {L("Choisir un forfait", "Choose a plan")} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-white/40 border-b border-white/10">
                      <th className="pb-2 font-medium">{L("Date", "Date")}</th>
                      <th className="pb-2 font-medium">{L("Modules", "Modules")}</th>
                      <th className="pb-2 font-medium text-right">{L("Montant", "Amount")}</th>
                      <th className="pb-2 font-medium text-right">{L("Statut", "Status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-white/5" data-testid={`invoice-${inv.id}`}>
                        <td className="py-3 text-white/80">{fmtDate(inv.date)}</td>
                        <td className="py-3 text-white/60">{(inv.modules || []).map((m) => NAME[m] || m).join(", ") || "—"}</td>
                        <td className="py-3 text-right font-mono text-white">{inv.amount} $</td>
                        <td className="py-3 text-right"><span className="rounded-full bg-green-500/12 border border-green-500/30 px-2 py-0.5 text-[11px] text-green-400">{L("Payé", "Paid")}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function RowB({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-white/8 pb-2 last:border-0">
      <span className="text-white/50">{k}</span>
      <span className="font-semibold text-white">{v}</span>
    </div>
  );
}
